// tokens/index.ts — Design tokens.
//
// P6 STEP 2 (token/visual foundation, 2026-06-06): rewritten to the redesign
// spec `docs/specs/ai-tutor-redesign-design.md` §3 (Codex-owned — read, never
// edited). The legacy ochre/sepia palette + 3-font trio (Source Serif 4 / Inter
// Tight / JetBrains Mono) are replaced by the violet→magenta brand + sage/rose/
// amber semantics + a single Inter variable font.
//
// COMPAT CONTRACT (spec §3.6 "Don't break consumers"): every token KEY that the
// ~193 existing consumers read is preserved — only the VALUES are remapped.
// Where the spec renames a concept we keep the old key as an alias pointing at
// the new value (e.g. `accent` now carries the solo brand-ink violet; the font
// aliases `serif/sans/mono/heading/body` all point at Inter). No key is removed.
//
// Sources, per spec section:
//   - Light palette ........ §3.1
//   - Dark palette ......... §3.2
//   - Brand gradient ....... §3.3
//   - Semantic colors ...... §3.4 (+ contrast-safe text variants)
//   - Readiness bands ...... §3.5 (MASTERY_BANDS x5, PROBABILITY_BANDS x3)
//   - Typography (Inter) ... §3.6
//   - Shape / radii ........ §3.7
// Breakpoints + responsive spacing: build-contract §B.

export const THEME = {
  // ── Typography (spec §3.6) ──────────────────────────────────────────────
  // Single family: Inter (variable, self-hosted). All historical font aliases
  // collapse onto Inter so no consumer compile-breaks. `fontSerif`/`fontMono`
  // are retained ONLY as back-compat aliases; warmth now comes from weight,
  // size, line-height and italic — not a serif.
  fontSans:  '"Inter", "Inter Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontSerif: '"Inter", "Inter Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontMono:  '"Inter", "Inter Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',

  // ── Shape / radii (spec §3.7) ───────────────────────────────────────────
  radius: { sm: 6, md: 12, lg: 14, xl: 20, pill: 999 } as const,

  // ── Light — DEFAULT theme (spec §3.1 / §3.3 / §3.4) ─────────────────────
  light: {
    // §3.1 surfaces + ink
    bg: '#f7f4fc',                                   // lavender-cream app bg
    surface: '#ffffff',                              // cards
    surface2: '#efeafa',                             // soft lavender step (raised/secondary fill)
    drawerBg: '#f1ecfb',                             // drawer panel (slightly cooler than bg)
    drawerHover: 'rgba(105,67,255,0.06)',            // violet-tinted hover
    line: 'rgba(26,21,53,0.06)',                     // hairline card borders (border)
    lineStrong: 'rgba(26,21,53,0.10)',              // inputs (border-strong)
    text: '#1a1535',                                 // primary text (ink)
    textMuted: '#6e6885',                            // secondary text (muted)
    textFaint: '#a39db8',                            // tertiary / metadata (faint)
    // §3.3 brand — solo brand ink (gradient lives in BRAND_GRADIENT below)
    accent: '#4a2e9e',                               // brand ink (violet)
    accentSoft: 'rgba(105,67,255,0.10)',             // violet tint fill
    accentBorder: 'rgba(105,67,255,0.40)',           // violet emphasis border (unread, active)
    accentInk: '#4a2e9e',                            // strong brand text
    // §3.4 semantics — fills/borders/icons/large numerals (NOT small text)
    right: '#2e8d6f',  rightSoft: 'rgba(46,141,111,0.12)',  rightText: '#1f6850',   // sage  (§3.4 text-variant ≥4.5:1)
    wrong: '#c5365b',  wrongSoft: 'rgba(197,54,91,0.10)',   wrongText: '#9a2a45',   // rose  (§3.4 text-variant ≥4.5:1)
    warn:  '#d4a04f',  warnSoft:  'rgba(212,160,79,0.14)',  warnText:  '#7a5a1c',   // amber (§3.4 text-variant ≥4.5:1)
    // chat / chrome
    profBubble: 'transparent', youBubble: '#efeafa', chip: '#ffffff',
  },

  // ── Dark — alternative theme (spec §3.2 / §3.3 / §3.4) ───────────────────
  dark: {
    // §3.2 surfaces + ink
    bg: '#0e0a18',                                   // near-black, violet undertone
    surface: '#16101f',                              // cards
    surface2: '#1e1730',                             // raised/secondary fill
    drawerBg: '#120c1d',                             // drawer panel
    drawerHover: 'rgba(124,90,255,0.10)',            // violet-tinted hover
    line: 'rgba(255,255,255,0.06)',                  // hairline borders
    lineStrong: 'rgba(255,255,255,0.12)',
    text: '#ece8f5',                                 // primary text (ink)
    textMuted: '#9a93b3',                            // secondary text (muted)
    textFaint: '#5e5878',                            // tertiary / metadata (faint)
    // §3.3 brand — dark solo brand ink
    accent: '#a89aff',                               // brand ink (light violet)
    accentSoft: 'rgba(124,90,255,0.18)',
    accentBorder: 'rgba(124,90,255,0.45)',           // violet emphasis border (unread, active)
    accentInk: '#c3b6ff',
    // §3.4 semantics — brighter ends for legibility on near-black (§3.5 gradient light ends)
    right: '#3aa882',  rightSoft: 'rgba(58,168,130,0.16)',  rightText: '#6fd0ac',   // sage
    wrong: '#e0566f',  wrongSoft: 'rgba(224,86,111,0.16)',  wrongText: '#ff96a6',   // rose
    warn:  '#e2b86b',  warnSoft:  'rgba(226,184,107,0.16)', warnText:  '#f0d49a',   // amber
    // chat / chrome
    profBubble: 'transparent', youBubble: '#221a33', chip: '#1e1730',
  },
} as const;

export type ThemeMode = 'dark' | 'light';
export type ThemeColors = typeof THEME.light;

// ── Brand gradient (spec §3.3) ─────────────────────────────────────────────
// The signature violet→magenta gradient. Mode-aware (dark is more saturated).
// Plus the saffron "spark" accent used in the Il Prof mark / small highlights.
export const BRAND_GRADIENT = {
  light: 'linear-gradient(135deg, #6943ff, #e547c4)',
  dark:  'linear-gradient(135deg, #7c5aff, #ff5ad8)',
} as const;

export const SPARK = {
  light: '#d4a04f',
  dark:  '#ffd76e',
} as const;

// ── Readiness bands — MASTERY (spec §3.5; build-contract D2 "5-tier everywhere")
// Five mastery bands as ONE exported source of truth. Each band carries the
// solid `color` (band tint) + `gradient` (start→end from §3.5) + the contrast-
// safe `textColor` for small labels + Italian `label`. `da-iniziare` is the
// uncolored empty state (white fill + border), visually distinct from the
// probability "grey" filled-low state (§3.5 note).
//
// NOTE: this is a NEW presentation layer (display-binning of mastery_score for
// the 25-cell map + drill-down rings). It does NOT change stored mastery, and
// per spec §3.5 it must NOT silently change `MasteryRing` thresholds — wiring
// the 5-band map into surfaces is STEP 3, not STEP 2.
export interface ReadinessBand {
  id: string;
  label: string;
  /** solid band color (light mode) */
  color: string;
  /** §3.5 gradient start→end (light mode) */
  gradient: string;
  /** contrast-safe color for small text on light bg (WCAG AA) */
  textColor: string;
  /** dark-mode solid color */
  colorDark: string;
  /** dark-mode contrast-safe text color */
  textColorDark: string;
}

export const MASTERY_BANDS = {
  forte: {
    id: 'forte', label: 'Forte',
    color: '#2e8d6f', gradient: 'linear-gradient(135deg, #2e8d6f, #3aa882)',
    textColor: '#1f6850', colorDark: '#3aa882', textColorDark: '#6fd0ac',
  },
  buono: {
    id: 'buono', label: 'Buono',
    color: '#6943ff', gradient: 'linear-gradient(135deg, #6943ff, #7c5aff)',
    textColor: '#4a2e9e', colorDark: '#7c5aff', textColorDark: '#a89aff',
  },
  daRinforzare: {
    id: 'da-rinforzare', label: 'Da rinforzare',
    color: '#d4a04f', gradient: 'linear-gradient(135deg, #d4a04f, #e2b86b)',
    textColor: '#7a5a1c', colorDark: '#e2b86b', textColorDark: '#f0d49a',
  },
  debole: {
    id: 'debole', label: 'Debole',
    color: '#c5365b', gradient: 'linear-gradient(135deg, #c5365b, #d84762)',
    textColor: '#9a2a45', colorDark: '#e0566f', textColorDark: '#ff96a6',
  },
  daIniziare: {
    id: 'da-iniziare', label: 'Da esplorare',   // L2 (founder-locked 2026-07-13): source-neutral — a lesson OR a simulation explores it
    // uncolored empty state: white fill + border (§3.5). textColor = muted ink.
    color: '#ffffff', gradient: 'none',
    textColor: '#6e6885', colorDark: '#16101f', textColorDark: '#9a93b3',
  },
} as const satisfies Record<string, ReadinessBand>;

export type MasteryBandId = keyof typeof MASTERY_BANDS;

// Ordered strongest→weakest→empty, for legends and map iteration.
export const MASTERY_BAND_ORDER: MasteryBandId[] = [
  'forte', 'buono', 'daRinforzare', 'debole', 'daIniziare',
];

// Founder-locked 5-band cutoffs (2026-06-06, P6 STEP 3d). Mastery is 0..1.
// Forte ≥0.85 · Buono 0.70–0.84 · Da rinforzare 0.40–0.69 · Debole <0.40 ·
// Da iniziare = not started (no principles, or none observed yet). The
// "strong" bar moved 0.70→0.85 (stricter, ~exam pass) — a deliberate change
// from the W6 v1.1 3-tier ring thresholds (spec §3.5 "explicit call").
export const MASTERY_BAND_CUTOFFS = { forte: 0.85, buono: 0.70, daRinforzare: 0.40 } as const;

export interface MasteryBandInput {
  /** average mastery, 0..1 */
  averageMastery: number;
  /** total principles in the unit (0 → never started) */
  principlesTotal?: number;
  /** observed principles (0 with total>0 → started-but-no-evidence → empty) */
  principlesObserved?: number;
}

/**
 * Map a 0..1 mastery score to its readiness band — the ONE source of truth for
 * the Progressi map, the rings, and the status labels (founder D2). Empty band
 * ('da iniziare') when the unit has no principles or none observed yet.
 */
export function masteryBand(input: MasteryBandInput): ReadinessBand & { id: string } {
  const { averageMastery, principlesTotal, principlesObserved } = input;
  if (principlesTotal === 0) return MASTERY_BANDS.daIniziare;
  if (principlesTotal !== undefined && principlesObserved === 0) return MASTERY_BANDS.daIniziare;
  if (averageMastery >= MASTERY_BAND_CUTOFFS.forte) return MASTERY_BANDS.forte;
  if (averageMastery >= MASTERY_BAND_CUTOFFS.buono) return MASTERY_BANDS.buono;
  if (averageMastery >= MASTERY_BAND_CUTOFFS.daRinforzare) return MASTERY_BANDS.daRinforzare;
  return MASTERY_BANDS.debole;
}

/** Mode-aware color + contrast-safe text color for a band (Progressi rings/cells). */
export function masteryBandColors(band: ReadinessBand, mode: ThemeMode): { color: string; textColor: string } {
  return mode === 'dark'
    ? { color: band.colorDark, textColor: band.textColorDark }
    : { color: band.color, textColor: band.textColor };
}

// ── Readiness bands — PROBABILITY d'esame (spec §3.5: THREE states only) ────
// Distinct mapping from mastery: green ≥80 / amber 50-79 / grey <50. NO violet
// "buono" band (violet is mastery-only). Grey is a FILLED desaturated state
// (active, low) — must stay visually distinct from mastery "da-iniziare" white,
// and must not read as disabled (§3.5 note).
export interface ProbabilityBand {
  id: string;
  label: string;
  /** inclusive lower bound (percent) for this band */
  min: number;
  color: string;
  soft: string;
  textColor: string;
  colorDark: string;
  textColorDark: string;
}

export const PROBABILITY_BANDS = {
  alta: {
    id: 'alta', label: 'Alta', min: 80,
    color: '#2e8d6f', soft: 'rgba(46,141,111,0.12)', textColor: '#1f6850',
    colorDark: '#3aa882', textColorDark: '#6fd0ac',
  },
  media: {
    id: 'media', label: 'Media', min: 50,
    color: '#d4a04f', soft: 'rgba(212,160,79,0.14)', textColor: '#7a5a1c',
    colorDark: '#e2b86b', textColorDark: '#f0d49a',
  },
  bassa: {
    id: 'bassa', label: 'Bassa', min: 0,
    // filled desaturated grey (NOT disabled, NOT white)
    color: '#8e8aa0', soft: 'rgba(142,138,160,0.16)', textColor: '#54506a',
    colorDark: '#6b6786', textColorDark: '#b4afc8',
  },
} as const satisfies Record<string, ProbabilityBand>;

export type ProbabilityBandId = keyof typeof PROBABILITY_BANDS;

/** Map a 0–100 probability to its band (green ≥80 / amber 50-79 / grey <50). */
export function probabilityBand(pct: number): ProbabilityBand {
  if (pct >= PROBABILITY_BANDS.alta.min) return PROBABILITY_BANDS.alta;
  if (pct >= PROBABILITY_BANDS.media.min) return PROBABILITY_BANDS.media;
  return PROBABILITY_BANDS.bassa;
}

// Minimum tap target everywhere (build-contract §B): 44×44px.
export const TAP_TARGET_MIN = 44;

// ── V29 shell metrics (v29-build-plan §2) ───────────────────────────────────
// Fixed chrome heights the shell and screens share; screens add these to their
// scroll-area bottom padding so content never hides under fixed bars.
export const TABBAR_H = 62;        // bottom tab bar (excl. safe-area inset)
export const READING_DOCK_H = 64;  // lesson/teoria reading dock (excl. inset)

export function getTokens(mode: ThemeMode) {
  return {
    ...THEME[mode],
    fonts: {
      // All aliases collapse onto Inter (spec §3.6). Keys retained for the
      // ~193 existing consumers; `serif`/`mono` are now Inter, not their old
      // families.
      serif:   THEME.fontSans,
      sans:    THEME.fontSans,
      mono:    THEME.fontSans,
      heading: THEME.fontSans,
      body:    THEME.fontSans,
    },
    radius: THEME.radius,
    // Mode-aware brand gradient + spark, available off the token object so
    // consumers don't have to import BRAND_GRADIENT/SPARK separately.
    brandGradient: BRAND_GRADIENT[mode],
    spark: SPARK[mode],
    mode,
  };
}

export type Tokens = ReturnType<typeof getTokens>;

// useTokens hook lives in state/theme.tsx. Import it from there, not here.
